import { logger } from '@/services/LoggingService';
import type { AssetType, NormalizedAsset } from '@/types/assets';
import {
  fetchStockFundamentals as searchStockFundamentals,
  fetchFundsRaw as searchFundsRaw,
  fetchFunds as searchFunds,
} from '@/services/assets/AssetSearchService';
import { formatAssetDisplay } from '@/utils/assets/normalize';

export type SearchParams = {
  selectedAssetType: AssetType;
  equitySource: 'stock' | 'fund';
  searchQuery: string;
};

export async function fetchAssetsFor({ selectedAssetType, equitySource, searchQuery }: SearchParams): Promise<NormalizedAsset[]> {
  let results: NormalizedAsset[] = [];

  try {
    switch (selectedAssetType) {
      case 'equity': {
        logger.debug('asset-search', 'Processing equity search', { equitySource });
        if (equitySource === 'stock') {
          const stockResults = await searchStockFundamentals(searchQuery);
          results = (stockResults || []).map((stock: any) => formatAssetDisplay(stock, 'stock_fundamentals'));
        } else {
          const equityFundResults = await searchFundsRaw('equity', searchQuery);
          results = (equityFundResults || []).map((fund: any) => formatAssetDisplay(fund, 'funds_raw'));
        }
        break;
      }

      case 'fixed_income': {
        const fixedIncomeResults = await searchFundsRaw('fixed_income', searchQuery);
        results = (fixedIncomeResults || []).map((fund: any) => formatAssetDisplay(fund, 'funds_raw'));
        break;
      }

      case 'cash': {
        const cashResults = await searchFundsRaw('cash', searchQuery);
        results = (cashResults || []).map((fund: any) => formatAssetDisplay(fund, 'funds_raw'));
        break;
      }

      case 'alternative': {
        const alternativeResults = await searchFundsRaw('alternative', searchQuery);
        const alternativeFunds = await searchFunds('alternative', searchQuery);
        results = [
          ...(alternativeResults || []).map((fund: any) => formatAssetDisplay(fund, 'funds_raw')),
          ...(alternativeFunds || []).map((fund: any) => formatAssetDisplay(fund, 'funds')),
        ];
        break;
      }

      case 'multi_asset': {
        const multiAssetResults = await searchFundsRaw('multi_asset', searchQuery);
        const multiAssetFunds = await searchFunds('multi_asset', searchQuery);
        results = [
          ...(multiAssetResults || []).map((fund: any) => formatAssetDisplay(fund, 'funds_raw')),
          ...(multiAssetFunds || []).map((fund: any) => formatAssetDisplay(fund, 'funds')),
        ];
        break;
      }

      default:
        logger.warn('asset-search', 'Unhandled asset type', { selectedAssetType });
        break;
    }

    logger.info('asset-search', 'Asset fetch completed', { count: results.length });
    results = results.sort((a, b) => a.name.localeCompare(b.name));
    return results;
  } catch (error) {
    logger.error('asset-search', 'Error in fetchAssetsFor', { error });
    throw error;
  }
}
